import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { formattedCurrency, notifyError, notifySuccess } from "utils";
import { useDictionary } from "hooks";
import { STATUS_OPTIONS } from "globals/constants";
import { isNonEmptyArray, isNonEmptyObject, isNonEmptyString, isNumber } from "@cayuse/shared-react.utils.type-checks";
import Button from "@cayuse/shared-react.components.button";
import IconButton from "@cayuse/shared-react.components.icon-button";
import Number from "@cayuse/shared-react.components.form.inputs.number";
import TextInput from "@cayuse/shared-react.components.form.inputs.text";
import Table from "components/Table";
import CustomDropdown from "@cayuse/shared-react.custom-dropdown";
import styles from "./styles.css";
import { api } from "services/api";

const INITIAL_VALUES = {
	number: "",
	distributed: "",
	status: ""
};
const NEW_ACCOUNT = "__newAccount__";
const AccountDistributionTable = ({
	distributions,
	award,
	notifyParentOnSave
}) => {
	const hasEntries = isNonEmptyArray(distributions);
	const [ editingId, setEditingId ] = useState("");
	const [ actualDistribution, setActualDistribution ] = useState({
		accountId: INITIAL_VALUES.number,
		amount: INITIAL_VALUES.distributed
	});
	const [ actualAccount, setActualAccount ] = useState({
		accountNumber: INITIAL_VALUES.number,
		statusKey: INITIAL_VALUES.status
	});
	const [ isAdding, setIsAdding ] = useState(false);
	const [ errorMessages, setErrorMessages ] = useState({});
	const [ accountStatusMap, setAccountStatusMap ] = useState({});
	const [ originalAccountStatusMap, setOriginalAccountStatusMap ] = useState({});
	const [ isEntryUpdating, setIsEntryUpdating ] = useState(false);
	const dictionary = useDictionary("awards.fundDistribution");

	const renderNoEntries = () => {
		return (
			<Table.Row>
				<Table.Cell colSpan="3">
					<i className={styles.noAccountsMessage}>{dictionary.emptyMsg ?? "Funds will apear here."}</i>
				</Table.Cell>
			</Table.Row>
		);
	};
	const renderValue = value => {
		return isNonEmptyString(value)
			? value
			: "--";
	};

	const getAccount = async id => {
		try {
			return await api.accounts.get({ id });

		} catch (error) {
			notifyError("Something went wrong. Please try again.", error);
		}
	};

	const resetAccount = () =>{
		setActualDistribution({ accountId: INITIAL_VALUES.number, amount: INITIAL_VALUES.distributed });
		setActualAccount({ accountNumber: INITIAL_VALUES.number, statusKey: INITIAL_VALUES.status });
		delete accountStatusMap[NEW_ACCOUNT];
		setErrorMessages({});
	};

	const onClickTimes = (id = "", fundTitle) =>{
		if (isEntryUpdating) {
			return;
		}

		if(isNonEmptyString(editingId)) {
			setAccountStatusMap(originalAccountStatusMap);
			setEditingId("");
		}else if(isAdding) {
			setIsAdding(false);
		}else{
			onDelete(id, fundTitle);
		}
		resetAccount();
	};

	const onDelete = async (id, fundTitle) =>{
		setIsEntryUpdating(true);
		try {
			await api.awards.removeDistribution(award.awardId, id);

			notifyParentOnSave();
			notifySuccess(
				`Successfully deleted ${fundTitle} distribution!`
			);
		} catch (error) {
			notifyError("Something went wrong. Please try again.", error);
		} finally {
			setIsEntryUpdating(false);
		}
	};

	const onSaveEdit = async fundTitle =>{
		const editedEntry = {
			number: actualAccount.accountNumber,
			distributed: parseFloat(actualDistribution.amount),
			status: actualAccount.statusKey
		};
		const errors = checkInvalidValues(editedEntry);
		if(isNonEmptyObject(errors)) {
			setErrorMessages(errors);
		}else{
			const id = actualDistribution.accountId;
			const updatedAccount = {
				statusKey: editedEntry.status,
				objectVersion: actualAccount.objectVersion
			};

			setIsEntryUpdating(true);

			try {
				await api.accounts.update({ id }, updatedAccount);

				await api.awards.updateDistribution(
					award.awardId,
					actualDistribution.distributionId,
					{
						amount: editedEntry.distributed,
					},
				);

				notifyParentOnSave();
				setEditingId("");
				resetAccount();
				notifySuccess(
					`Successfully updated ${fundTitle} distribution!`,
					`New distributed amount: ${editedEntry.distributed}.`
				);
			} catch (error) {
				notifyError("Something went wrong. Please try again.", error);
			} finally {
				setIsEntryUpdating(false);
			}
		}
	};

	const onSaveAdd = async () => {
		const newEntry = {
			number: actualAccount.accountNumber,
			distributed: parseFloat(actualDistribution.amount),
			status: actualAccount.statusKey
		};
		const errors = checkInvalidValues(newEntry);
		if (isNonEmptyObject(errors)) {
			setErrorMessages(errors);
		} else {
			setIsEntryUpdating(true);

			let accountId;
			let existingAccounts;

			try {
				existingAccounts = await api.accounts.find({ number: newEntry.number });
			} catch (error) {
				notifyError("Something went wrong. Please try again.", error);
			}

			const matchingAccount = existingAccounts.content?.find(account => account.accountNumber === newEntry.number);
			const hasExistingAccount = isNonEmptyObject(matchingAccount);

			setIsEntryUpdating(true);

			if (hasExistingAccount) {
				accountId = matchingAccount.accountId;

				try {
					await api.accounts.update(
						{ id: accountId },
						{
							statusKey: newEntry.status,
							objectVersion: matchingAccount.objectVersion,
						},
					);
				} catch (error) {
					notifyError("Something went wrong. Please try again.", error);
				}

			} else {
				try {
					const response = await api.accounts.create({
						accountNumber: newEntry.number,
						statusKey: newEntry.status,
					});

					accountId = response.accountId;
				} catch (error) {
					notifyError("Something went wrong. Please try again.", error);
				}
			}

			try {
				await api.awards.addDistribution(
					award.awardId,
					{
						accountId: accountId,
						amount: newEntry.distributed,
					},
				);

				notifyParentOnSave();
				setIsAdding(false);
				resetAccount();
				notifySuccess(
					`Successfully added new distribution!`,
				);
			} catch (error) {
				notifyError("Something went wrong. Please try again.", error);
			}
		}
		setIsEntryUpdating(false);
	};

	const onSave = fundTitle =>{
		if (isEntryUpdating) {
			return;
		}

		if (isNonEmptyString(editingId)) {
			onSaveEdit(fundTitle);
		}else if (isAdding) {
			onSaveAdd();
		}
	};

	const checkInvalidValues = account =>{
		const errors = {};
		const hasWhiteSpace = (/\s/.test(account.number));
		const hasSpecialCharacters = (/[ `!@#$%^&*()+=[\]{};':"\\|,.<>/?~]/.test(account.number));
		const invalidInitialCharacter = account.number.startsWith("-") || account.number.startsWith("_");

		switch(true) {
		case(account.distributed < 0 ):
		case(!isNumber(account.distributed)):
		case(isNaN(account.distributed)):
			errors.distribution = dictionary.currencyValueMsg ?? "Please enter a currency value.";
			break;
		default:
		}

		switch(true) {
		case(hasWhiteSpace && isAdding):
			errors.number = dictionary.removeExtraspacesMsg ?? "Please remove extra spaces and try again.";
			break;
		case(hasSpecialCharacters && isAdding):
			errors.number = dictionary.invalidSpecSymbolsMsg ?? "The only special characters allowed are _ and -";
			break;
		case(invalidInitialCharacter && isAdding):
			errors.number = dictionary.specSymbolsMsg ?? "Fund number may not start with a _ or -";
			break;
		case(!isNonEmptyString(account.number) && isAdding):
			errors.number = dictionary.enterValidFundMsg ?? "Please enter a valid fund number";
			break;
		default:
		}

		switch(true) {
		case(!isNonEmptyString(account.status)):
			errors.status = dictionary.emptyStatusMsg ?? "Please select an fund status.";
			break;
		default:
		}

		return errors;
	};

	const onClickEdit = async distributionId =>{
		const distribution = distributions.find(element => element.distributionId === distributionId);
		const account = await getAccount(distribution.accountId);
		setActualDistribution(distribution);
		setActualAccount(account);
		setEditingId(distribution.distributionId);
	};

	const fetchAccountStatus = useCallback (distributions =>{
		const updatedStatusMap = {};

		if(isNonEmptyArray(distributions)) {
			return Promise.all(distributions.map(distribution =>{
				return getAccount(distribution.accountId);
			}))
				.then(all =>{
					all.forEach( account =>{
						const status = account.statusKey;
						if(isNonEmptyString(status)) {
							updatedStatusMap[account.accountNumber] = STATUS_OPTIONS.find(element=> element.key === status).text;
						}
					});
					setAccountStatusMap(updatedStatusMap);
					setOriginalAccountStatusMap(updatedStatusMap);
				});
		}else{
			return [];
		}
	},[]);

	const onChangeDistributed = value =>{
		setActualDistribution({ ...actualDistribution, amount: value });
	};

	const onChangeAccountNumber = value => {
		setActualAccount({ ...actualAccount, accountNumber: value });
	};

	const onStatusChange = (event, { value: status }) => {
		const number = isAdding
			? NEW_ACCOUNT
			: actualAccount.accountNumber;
		if (isNonEmptyString(number)) {
			const newStatus = STATUS_OPTIONS.find(element => element.text === status);
			if(isNonEmptyObject(newStatus)) {
				const updatedStatutsMap = { ...accountStatusMap, [number]:newStatus.text };
				setAccountStatusMap(updatedStatutsMap);
				let account = { ...actualAccount };
				account.statusKey = newStatus.key;
				setActualAccount({ ...actualAccount, statusKey: newStatus.key });
			}
		}
	};

	useEffect(()=>{
		fetchAccountStatus(distributions);
	},[ distributions, fetchAccountStatus ]);

	const renderAccountRow = (entry, index) => {
		const isEditing = entry.distributionId === editingId;

		return (
			<Table.Row key={`account-entry-${index}`}>
				<Table.Cell>
					{renderValue(entry.accountNumber)}
				</Table.Cell>
				<Table.Cell>
					{isEditing
						? <>
							<Number
								className={styles.inputBox}
								defaultValue= {entry.amount.toString()}
								onChange={event =>onChangeDistributed(event.target.value)}
								aria-label="Distributed amount"
								required={true}
								autoFocus
							/>
							<div aria-live="polite">
								{isNonEmptyString(errorMessages.distribution)
									? <span className={styles.validationMessage}>
										{errorMessages.distribution}
									</span>  : ""}
							</div>
						</>
						: formattedCurrency(renderValue(entry.amount.toString()))}
				</Table.Cell>
				<Table.Cell>
					{isEditing ?
						<div role="application">
							<CustomDropdown
								customClassname={styles.statusDropdown}
								options={STATUS_OPTIONS}
								onChange={onStatusChange}
								value={accountStatusMap[entry.accountNumber]}
								ariaLabel="Select fund status required"
								isCustomized={true}
								isCustomStyling={true}
								isCustomOnChange={true}
							/>
						</div>
						: accountStatusMap[entry.accountNumber] }
				</Table.Cell>
				<Table.Cell className={styles.actions}>
					{isEditing
						? <>
							<Button
								busy={isEntryUpdating}
								medium
								onClick={() => {
									onSave(entry.accountNumber);
								}}
								primary
							>
								{dictionary.saveBtn ?? "Save"}
							</Button>

						</>
						:
						<IconButton
							tertiary
							className={styles.editButton}
							disabled={isAdding}
							aria-label={`Edit ${entry.accountNumber}`}
							icon="pencil"
							onClick={()=> onClickEdit(entry.distributionId)}
						/>
					}
					<IconButton
						tertiary
						className={styles.deleteButton}
						aria-label={`Delete ${entry.accountNumber}`}
						icon="times"
						onClick={()=> onClickTimes(entry.distributionId, entry.accountNumber)}
					/>
				</Table.Cell>
			</Table.Row>
		);
	};

	const renderAddAccountRow = (index = "addNew") =>{
		return (
			<Table.Row key={`account-entry-${index}`}>
				<Table.Cell>
					<TextInput
						autoFocus
						className={styles.inputBox}
						onChange={event =>onChangeAccountNumber(event.target.value)}
						aria-label="Account number"
						required={true}
					/>
					<div aria-live="polite">
						{isNonEmptyString(errorMessages.number)
							? <span className={styles.validationMessage}>
								{errorMessages.number}
							</span>  : ""}
					</div>
				</Table.Cell>
				<Table.Cell>
					<Number
						className={styles.inputBox}
						onChange={event =>onChangeDistributed(event.target.value)}
						aria-label="Distributed amount"
						required={true}
					>
					</Number>
					<div aria-live="polite">
						{isNonEmptyString(errorMessages.distribution)
							? <span className={styles.validationMessage}>
								{errorMessages.distribution}
							</span>  : ""}
					</div>
				</Table.Cell>
				<Table.Cell>
					<div role="application">
						<CustomDropdown
							customClassname={styles.statusDropdown}
							options={STATUS_OPTIONS}
							onChange={onStatusChange}
							value={accountStatusMap[NEW_ACCOUNT] || "Select fund status"}
							ariaLabel="Select fund status required"
							isCustomized={true}
							isCustomStyling={true}
							isCustomOnChange={true}
						/>
					</div>
					<div aria-live="polite">
						{isNonEmptyString(errorMessages.status)
							? <span className={styles.validationMessage}>
								{errorMessages.status}
							</span>  : ""}
					</div>
				</Table.Cell>
				<Table.Cell className={styles.actions}>
					<Button
						medium
						onClick={onSave}
						busy={isEntryUpdating}
						primary
					>
						{dictionary.saveBtn || "Save"}
					</Button>
					<IconButton tertiary className={styles.deleteButton} icon="times" aria-label="Cancel" onClick={onClickTimes}/>
				</Table.Cell>
			</Table.Row>
		);
	};

	const renderTableBody = () => {
		if (hasEntries) {
			return distributions.map(renderAccountRow);
		}else if (!isAdding) {
			return renderNoEntries();
		}
	};

	return (
		<div>
			<Table data-component="AccountDistributionTable" className={styles.accountDistributionTable} overflow="visible" captionText="Account Distribution">
				<Table.Header>
					<Table.Row>
						<Table.HeaderCell className={styles.headerCellStyle}>
							{dictionary.fundTableHeader ?? "Fund"}{(isAdding || editingId) && <span>*</span>}
						</Table.HeaderCell>
						<Table.HeaderCell className={styles.headerCellStyle}>
							{dictionary.distributedTableHeader ?? "Distributed"}{(isAdding || editingId) && <span>*</span>}
						</Table.HeaderCell>
						<Table.HeaderCell className={styles.headerCellStyle}>
							{dictionary.statusTableHeader ?? "Fund Status"}{(isAdding || editingId) && <span>*</span>}
						</Table.HeaderCell>
						<Table.HeaderCell className={styles.headerCellStyle}>
						</Table.HeaderCell>
					</Table.Row>
				</Table.Header>
				<Table.Body>
					{renderTableBody()}
					{isAdding
						? renderAddAccountRow()
						: ""}
				</Table.Body>
			</Table>
			<IconButton className={styles.button}
				disabled={isAdding || isNonEmptyString(editingId)}
				icon="plus"
				labelPosition="left"
				primary
				onClick={()=>setIsAdding(true)}
			>
				{ dictionary.addBtn ?? "Add Fund" }
			</IconButton>
		</div>
	);
};
AccountDistributionTable.propTypes = {
	totalExpected: PropTypes.number,
	obligated: PropTypes.number,
	distributed: PropTypes.number
};

export default AccountDistributionTable;
